import { initialsOf } from '@/lib/format';
import { STATION_TYPES, type StationType } from '@/config/brand';
import type {
  AppUser,
  AttendanceRecord,
  Booking,
  BookingStatus,
  CardTap,
  Club,
  FoodOrder,
  Member,
  MembershipPlan,
  MenuItem,
  Payment,
  PaymentMethod,
  RateWindow,
  Role,
  Session,
  Station,
  WalletTransaction,
} from '@/types/domain';

/** The existing DB is single-club; we synthesize one club id for the app. */
export const SUPA_CLUB_ID = 'bpc';

type Row = Record<string, any>;

export const rupeesToPaise = (n: unknown): number => Math.round((Number(n) || 0) * 100);
export const paiseToRupees = (p: unknown): number => Math.round(Number(p) || 0) / 100;

// ---------------------------------------------------------------------------
// Club (synthetic — no clubs table in the existing schema)
// ---------------------------------------------------------------------------
export function synthClub(): Club {
  const opened = new Date();
  opened.setHours(9, 0, 0, 0);
  return {
    id: SUPA_CLUB_ID,
    name: 'Break Point Club',
    shortCode: 'BPC',
    status: 'OPEN',
    openedAt: opened.toISOString(),
    tier: 'PREMIUM',
  };
}

// ---------------------------------------------------------------------------
// Stations (from `tables`; status is DERIVED from any live session)
// ---------------------------------------------------------------------------
const STATION_SORT: Record<string, number> = { 'pool-1': 1, 'pool-2': 2, 'mini-snooker': 3, snooker: 4 };

export function mapStation(row: Row, live?: Session | null): Station {
  const status = live ? (live.status === 'PAUSED' ? 'PAUSED' : 'IN_USE') : 'AVAILABLE';
  const type = STATION_TYPES.includes(row.type) ? (row.type as StationType) : 'POOL';
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    name: row.name,
    type,
    status,
    gpioPin: row.gpio_pin ?? null,
    sortOrder: STATION_SORT[row.id] ?? 99,
  };
}

// ---------------------------------------------------------------------------
// Users (from `profiles`)
// ---------------------------------------------------------------------------
export function roleIn(dbRole: string): Role {
  switch (dbRole) {
    case 'admin':
      return 'MASTER_ADMIN';
    case 'billing':
      return 'MANAGER';
    default:
      return 'STAFF'; // 'food' and anything else
  }
}

export function mapUser(row: Row): AppUser {
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    name: row.display_name ?? row.email,
    email: row.email,
    role: roleIn(row.role),
    status: row.active ? 'ACTIVE' : 'PENDING',
    initials: initialsOf(row.display_name ?? row.email ?? '?'),
    createdAt: row.created_at,
  };
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------
export function mapSession(row: Row, ratePerMinutePaise: number): Session {
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    stationId: row.table_id,
    status: row.status,
    startedAt: row.started_at,
    endedAt: row.ended_at ?? null,
    ratePerMinutePaise,
    pausedMs: (Number(row.paused_seconds) || 0) * 1000,
    pausedAt: row.paused_at ?? null,
    customerName: row.customer_name ?? null,
    playerCount: row.player_count ?? 2,
    note: null,
  };
}

// ---------------------------------------------------------------------------
// Menu (from `menu_items`; kind food|cigarette + category text)
// ---------------------------------------------------------------------------
export function mapMenuItem(row: Row): MenuItem {
  const category =
    row.kind === 'cigarette'
      ? 'BAR'
      : /drink|beverage|coffee|tea|juice/i.test(row.category ?? '')
        ? 'BEVERAGE'
        : 'FOOD';
  const emoji = { FOOD: '🍽️', BEVERAGE: '🥤', BAR: '🚬', SNACK: '🍿' }[category] ?? null;
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    name: row.name,
    category,
    pricePaise: rupeesToPaise(row.price),
    available: !!row.active,
    emoji,
  };
}

// ---------------------------------------------------------------------------
// Rate windows (one existing row carries all 3 type rates → expand to 3)
// ---------------------------------------------------------------------------
export function expandRateWindow(row: Row): RateWindow[] {
  const rateFor: Record<StationType, unknown> = {
    POOL: row.rate_pool,
    MINI_SNOOKER: row.rate_mini_snooker,
    SNOOKER: row.rate_snooker,
  };
  return STATION_TYPES.map((type) => ({
    id: `${row.id}:${type}`,
    clubId: SUPA_CLUB_ID,
    stationType: type,
    label: row.label ?? 'Rate',
    startMinute: row.start_min,
    endMinute: row.end_min,
    ratePerMinutePaise: rupeesToPaise(rateFor[type]),
    daysOfWeek: [],
    active: true,
  }));
}

// ---------------------------------------------------------------------------
// Food orders (group flat `session_orders` rows into one order per session)
// ---------------------------------------------------------------------------
export function groupSessionOrders(rows: Row[], sessionId: string, active: boolean): FoodOrder {
  return {
    id: `${sessionId}:order`,
    clubId: SUPA_CLUB_ID,
    sessionId,
    lines: rows.map((r) => ({
      menuItemId: r.item_id,
      name: r.item_name,
      unitPricePaise: rupeesToPaise(r.unit_price),
      quantity: r.qty,
    })),
    status: active ? 'OPEN' : 'COMPLETED',
    createdAt: rows[0]?.created_at ?? new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------
export function bookingStatusIn(s: string): BookingStatus {
  switch (s) {
    case 'FULFILLED':
      return 'SEATED';
    case 'CANCELLED':
      return 'CANCELLED';
    default:
      return 'CONFIRMED'; // SCHEDULED
  }
}

export function bookingStatusOut(s: BookingStatus): string {
  switch (s) {
    case 'SEATED':
      return 'FULFILLED';
    case 'CANCELLED':
      return 'CANCELLED';
    default:
      return 'SCHEDULED'; // PENDING | CONFIRMED
  }
}

export function mapBooking(row: Row, stationType: StationType | null): Booking {
  const durationMinutes =
    row.starts_at && row.ends_at
      ? Math.max(0, Math.round((new Date(row.ends_at).getTime() - new Date(row.starts_at).getTime()) / 60000))
      : 60;
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    stationId: row.table_id ?? null,
    stationType,
    customerName: row.customer_name,
    phone: row.phone ?? null,
    partySize: 1,
    startAt: row.starts_at,
    durationMinutes,
    status: bookingStatusIn(row.status),
    createdAt: row.created_at,
    createdBy: row.created_by ?? null,
  };
}

// ---------------------------------------------------------------------------
// Payments (the old app records payment on the session row itself)
// ---------------------------------------------------------------------------
export function methodIn(pm: string | null): PaymentMethod {
  switch (pm) {
    case 'ONLINE':
      return 'UPI';
    case 'MEMBERSHIP':
      return 'MEMBERSHIP';
    case 'COMP':
      return 'COMP';
    case 'PENDING':
      return 'PENDING';
    case 'MIX':
      return 'MIX';
    default:
      return 'CASH';
  }
}

/** App method → DB value. UPI is stored as ONLINE to match existing data. */
export function methodOut(pm: PaymentMethod): string {
  return pm === 'UPI' ? 'ONLINE' : pm;
}

export function sessionToPayment(row: Row): Payment {
  return {
    id: `${row.id}:pay`,
    clubId: SUPA_CLUB_ID,
    sessionId: row.id,
    bookingId: null,
    memberId: row.member_id ?? null,
    customerName: row.customer_name ?? row.closed_by_name ?? null,
    amountPaise: rupeesToPaise(row.total_amount),
    method: methodIn(row.payment_method ?? null),
    mix: null,
    status: 'PAID',
    createdAt: row.ended_at ?? row.created_at,
  };
}

// ---------------------------------------------------------------------------
// Members, wallet, plans, taps
// ---------------------------------------------------------------------------
export function mapMember(row: Row): Member {
  const cards = (row.member_cards ?? []) as Row[];
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    name: row.name,
    phone: row.phone ?? null,
    cards: cards.map((c) => ({
      uid: c.uid,
      label: c.label,
      role: c.role === 'ADMIN' ? 'ADMIN' : 'MEMBER',
      addedAt: c.added_at,
    })),
    walletBalancePaise: rupeesToPaise(row.wallet_balance),
    status: row.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
    planId: row.plan_id ?? null,
    joinedAt: row.joined_at,
  };
}

export function mapWalletTxn(row: Row): WalletTransaction {
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    memberId: row.member_id,
    type: row.type,
    amountPaise: rupeesToPaise(row.amount),
    balanceAfterPaise: rupeesToPaise(row.balance_after),
    note: row.note ?? null,
    sessionId: row.session_id ?? null,
    paymentId: null,
    createdAt: row.created_at,
  };
}

export function mapPlan(row: Row): MembershipPlan {
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    name: row.name,
    pricePaise: rupeesToPaise(row.price),
    billingPeriod: row.billing_period ?? 'MONTHLY',
    benefits: row.benefits ?? [],
    status: row.status === 'ACTIVE' ? 'ACTIVE' : 'COMING_SOON',
  };
}

export function mapCardTap(row: Row): CardTap {
  return { uid: (row.uid ?? '').toUpperCase(), at: row.created_at, readerId: row.reader_id ?? null };
}

// ---------------------------------------------------------------------------
// Attendance
// ---------------------------------------------------------------------------
export function mapAttendance(row: Row): AttendanceRecord {
  return {
    id: row.id,
    clubId: SUPA_CLUB_ID,
    receptionistId: row.receptionist_id,
    receptionistName: row.receptionist_name,
    workDate: row.work_date,
    clockInAt: row.clock_in_at,
    clockOutAt: row.clock_out_at ?? null,
  };
}
